'use client';

import * as React from 'react';

import { cn } from '../lib/utils';
import { Separator } from './separator';
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from './sidebar';

/**
 * Shared shell layout for L3 "sidebar apps" (platform/app, platform/admin, and
 * any future app with a sidebar). Wraps SidebarProvider + SidebarInset so
 * every consumer gets the same chrome, and exposes composable sub-parts
 * (Header + Main) so apps can customize what lives inside the inset.
 *
 * The `sidebar` prop receives the app's own `<AppSidebar>` — app-specific
 * content (nav items, groups) stays in each app. The shell is purely layout.
 *
 * Usage:
 *
 *   <AppShell sidebar={<AppSidebar />}>
 *     <AppShell.Header label={routeLabel} />
 *     <AppShell.Main>
 *       <Routes>...</Routes>
 *     </AppShell.Main>
 *     <Toaster />
 *   </AppShell>
 */

interface AppShellProps {
  /** App-specific sidebar (typically a `<Sidebar>` composition from @loop3/ui). */
  sidebar: React.ReactNode;
  /** Content of the inset (typically AppShell.Header + AppShell.Main). */
  children: React.ReactNode;
  /** Forwarded to the outer SidebarProvider if needed. */
  className?: string;
  /** Initial open state for the sidebar. Defaults to true (expanded). */
  defaultOpen?: boolean;
}

function AppShellRoot({ sidebar, children, className, defaultOpen }: AppShellProps) {
  return (
    <SidebarProvider className={className} defaultOpen={defaultOpen}>
      {sidebar}
      {/* min-w-0 lets the inset shrink below its content's intrinsic width so
       * wide children (data grids) scroll inside their own overflow containers
       * instead of forcing page-level horizontal scroll. */}
      <SidebarInset className="min-w-0">{children}</SidebarInset>
    </SidebarProvider>
  );
}

interface AppShellHeaderProps {
  /**
   * Shown next to the sidebar trigger. A string renders as the eyebrow; anything
   * else renders as given, which is how a route passes a `BreadcrumbTrail`.
   */
  label: React.ReactNode;
  /** Optional right-aligned slot (e.g. an action button). */
  rightSlot?: React.ReactNode;
  className?: string;
}

function AppShellHeader({ label, rightSlot, className }: AppShellHeaderProps) {
  return (
    <header
      className={cn(
        'flex h-14 shrink-0 items-center gap-2 border-b border-border px-4',
        className,
      )}
    >
      <SidebarTrigger className="-ml-1" />
      <Separator orientation="vertical" className="mr-2 h-4" />
      {typeof label === 'string' ? (
        <span className="eyebrow flex-1 opacity-60">{label}</span>
      ) : (
        // A trail renders a <nav>, which may not sit inside the eyebrow <span>.
        // The eyebrow's type is restated rather than reused: a trail carries more
        // text than a route name, so its size steps with the viewport.
        <div className="min-w-0 flex-1 font-mono text-[0.5625rem] font-medium tracking-[0.12em] uppercase opacity-60 lg:text-[0.625rem] xl:text-[0.6875rem] 2xl:text-[0.75rem]">
          {label}
        </div>
      )}
      {rightSlot}
    </header>
  );
}

interface AppShellMainProps {
  children: React.ReactNode;
  className?: string;
}

function AppShellMain({ children, className }: AppShellMainProps) {
  return <main className={cn('px-6 py-8', className)}>{children}</main>;
}

export const AppShell = Object.assign(AppShellRoot, {
  Header: AppShellHeader,
  Main: AppShellMain,
});

export type { AppShellProps, AppShellHeaderProps, AppShellMainProps };
